/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 */

/**
 * Event Log Panel — right pane of the Run Viewer.
 *
 * Reverse-chronological list of every SSE frame. Each entry is
 * collapsed to its name + summary; clicking expands the JSON payload.
 * In Demo Mode we hide the raw JSON and surface only a one-line summary
 * (the events themselves still arrive; we just don't show the bytes).
 *
 * Controls: All / Stage / Tool / LLM / Audit filter pills, search box,
 * pause-autoscroll toggle.
 */

import React, { useMemo, useState } from 'react';
import { isDemoMode } from '../../../utils/demoMode';

const FILTERS = [
  { id: 'all',   label: 'All',    test: () => true },
  { id: 'stage', label: 'Stage',  test: (e) => /^stage\./.test(e.event) },
  { id: 'tool',  label: 'Tool',   test: (e) => /^tool\./.test(e.event) },
  { id: 'llm',   label: 'LLM',    test: (e) => /^(response|act|reason|llm)\./.test(e.event) },
  { id: 'audit', label: 'Audit',  test: (e) => /^(audit|run|persona|connection)\./.test(e.event) },
];

function formatTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString();
  } catch (_) {
    return iso;
  }
}

function severityClass(sev) {
  if (sev === 'error') return 'wf-evt-row wf-evt-error';
  if (sev === 'warning') return 'wf-evt-row wf-evt-warning';
  if (sev === 'success') return 'wf-evt-row wf-evt-success';
  return 'wf-evt-row';
}

/**
 * Compose a short summary so the log is readable without expanding.
 */
function summarize(entry) {
  const { event, data } = entry;
  if (event === 'tool.invoked') return data?.tool_name || data?.tool_id || 'tool';
  if (event === 'tool.completed') {
    const tail = data?.latency_ms ? ` (${data.latency_ms}ms)` : '';
    return `${data?.tool_name || data?.tool_id || 'tool'}${tail}`;
  }
  if (event === 'stage.started') return data?.name || data?.stage_id || '';
  if (event === 'stage.completed') {
    const tail = data?.latency_ms ? ` (${data.latency_ms}ms)` : '';
    return `${data?.name || data?.stage_id || ''}${tail}`;
  }
  if (event === 'persona.loaded') return data?.display_name || data?.persona_id || '';
  if (event === 'response.token') return data?.token || '';
  if (event === 'connection.reconnecting') return `attempt ${data?.attempt}, retry in ${data?.delay_ms}ms`;
  if (event === 'error') return data?.message || '';
  if (data?.message) return data.message;
  return '';
}

function EventRow({ entry, demo }) {
  const [expanded, setExpanded] = useState(false);
  const summary = summarize(entry);
  return (
    <li className={severityClass(entry.severity)}>
      <button
        type="button"
        className="wf-evt-row-head"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        <span className="wf-evt-time">{formatTime(entry.at)}</span>
        <span className="wf-evt-name">{entry.event}</span>
        {entry.stageId && !demo && (
          <span className="wf-evt-stage">{entry.stageId}</span>
        )}
        {summary && <span className="wf-evt-summary">{summary}</span>}
      </button>
      {expanded && !demo && (
        <pre className="wf-evt-payload">
          {JSON.stringify(entry.data, null, 2)}
        </pre>
      )}
    </li>
  );
}

export default function EventLogPanel({ events, connection }) {
  const demo = isDemoMode();
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [paused, setPaused] = useState(false);

  const filtered = useMemo(() => {
    const f = FILTERS.find((x) => x.id === filter) || FILTERS[0];
    const q = search.trim().toLowerCase();
    return events.filter((e) => {
      if (!f.test(e)) return false;
      if (!q) return true;
      const hay = `${e.event} ${e.stageId || ''} ${summarize(e)}`.toLowerCase();
      return hay.includes(q);
    });
  }, [events, filter, search]);

  // When paused, we freeze the view by capping at the first N entries
  // that existed before the pause toggled. Cheap approximation: just
  // render the snapshot at the current event count.
  const view = paused ? filtered.slice(0, Math.min(filtered.length, events.length)) : filtered;

  return (
    <section className="wf-evt-panel">
      <header className="wf-evt-header">
        <h2 className="wf-detail-pane-title">Events</h2>
        <span className={`wf-evt-connection wf-evt-connection-${connection}`}>
          {connection}
        </span>
      </header>

      <div className="wf-evt-filters" role="tablist">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            role="tab"
            aria-selected={filter === f.id}
            className={`wf-evt-filter-pill ${filter === f.id ? 'is-active' : ''}`}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="wf-evt-controls">
        <input
          type="search"
          className="wf-evt-search"
          placeholder="Search events…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button
          type="button"
          className={`wf-evt-pause ${paused ? 'is-paused' : ''}`}
          onClick={() => setPaused((v) => !v)}
          title={paused ? 'Resume auto-update' : 'Pause auto-update'}
        >
          {paused ? '▶ Resume' : '❚❚ Pause'}
        </button>
      </div>

      {view.length === 0 ? (
        <p className="wf-evt-empty">No events match.</p>
      ) : (
        <ul className="wf-evt-list">
          {view.map((entry) => (
            <EventRow key={entry.id} entry={entry} demo={demo} />
          ))}
        </ul>
      )}
    </section>
  );
}
